import React from 'react'
import { Helmet } from 'react-helmet'
import PropTypes from 'prop-types'
import { StaticQuery, graphql } from 'gatsby'

const SEO = ({
  seoTitle,
  canonicalUrl,
  description,
  seoImage,
  slug,
  type,
  node_locale,
  ogLocale,
  ogType,
  ogDescription,
  ogImageAlt,
  feature_image,
  jobTitle,
  jobDescription,
  hideFromSearch,
}) => {
  return (
    <StaticQuery
      query={query}
      render={({
        site: {
          siteMetadata: {
            siteUrl,
            defaultImage,
            defaultImageWidth,
            defaultImageHeight,
            defaultOgLocale,
            defaultOgType,
            ogImage,
            defaultTwitterUserName,
            defaultTwitterCard,
            defaultOgSite_name,
            defaultImageAlt,
            facebookAppId,
            blogHomeTitle,
            blogHomeDescription,
            blogHomeImageUrl,
            blogHomeImageWidth,
            blogHomeImageHeight,
            blogHomeImageAltText,
          },
        },
      }) => {
        // define prop getter methods
        const getTitle = () => {
          return type === 'job'
            ? jobTitle
            : type === 'blogHome'
            ? `${blogHomeTitle} | Ouster`
            : seoTitle
            ? `${seoTitle} | Ouster`
            : null
        }

        const getDescription = () => {
          return type === 'job'
            ? jobDescription
            : type === 'blogHome'
            ? blogHomeDescription
            : description
        }

        const getImage = () => {
          if (type === 'blogHome') {
            return blogHomeImageUrl
          }

          if (type === 'blog') {
            return feature_image.url
          }

          if (seoImage && seoImage?.file?.url) {
            return seoImage.file.url.startsWith('//')
              ? `https:${seoImage.file.url}`
              : seoImage.file.url
          }

          if (
            seoImage &&
            seoImage?.images?.fallback?.src.startsWith('/static/')
          ) {
            return seoImage?.images?.fallback?.src.startsWith('/static/')
              ? `https://ouster.com${seoImage?.images?.fallback?.src}`
              : defaultImage
          }

          return defaultImage
        }

        const getUrl = () => {
          return `${siteUrl}${
            node_locale && node_locale.toLowerCase() !== 'en-us'
              ? '/' + node_locale
              : ''
          }${slug || '/'}`
        }

        const getCanonicalUrl = () => {
          if (canonicalUrl) {
            canonicalUrl = canonicalUrl.startsWith('http')
              ? canonicalUrl
              : `${siteUrl}${canonicalUrl}`
          } else {
            canonicalUrl = getUrl()
          }
          return canonicalUrl.endsWith('/') ? canonicalUrl : `${canonicalUrl}/`
        }

        const getNodeLocale = () => {
          return `${node_locale}`
        }

        const getOgLocale = () => {
          return `${ogLocale || defaultOgLocale}`
        }

        const getOgType = () => {
          return `${type !== 'blog' ? defaultOgType || ogType : 'article'}`
        }

        const getOgTitle = () => {
          return type === 'job'
            ? jobTitle
            : type === 'blogHome'
            ? blogHomeTitle
            : seoTitle
        }

        const getOgDescription = () => {
          return ogDescription || getDescription()
        }

        const getOgImage = () => {
          return type !== 'blog' ? getImage() : feature_image.url
        }

        const getOgImageWidth = () => {
          return type === 'blogHome'
            ? blogHomeImageWidth
            : type !== 'blog' && ogImage && ogImage?.file?.details?.image.width
            ? ogImage.file.details.image.width
            : type !== 'blog' &&
              seoImage &&
              seoImage?.file?.details?.image.width
            ? seoImage.file.details.image.width
            : type !== 'blog' &&
              seoImage &&
              seoImage?.images?.fallback?.src.startsWith('/static/')
            ? seoImage.width
            : type === 'blog'
            ? null
            : defaultImageWidth
        }

        const getOgImageHeight = () => {
          return type === 'blogHome'
            ? blogHomeImageHeight
            : type !== 'blog' && ogImage && ogImage?.file?.details?.image.height
            ? ogImage.file.details.image.height
            : type !== 'blog' &&
              seoImage &&
              seoImage?.file?.details?.image.height
            ? seoImage.file.details.image.height
            : type !== 'blog' && ogImage && ogImage?.file?.details?.image.height
            ? seoImage.file.details.image.height
            : type !== 'blog' &&
              seoImage &&
              seoImage?.images?.fallback?.src.startsWith('/static/')
            ? seoImage.height
            : type === 'blog'
            ? null
            : defaultImageHeight
        }

        const getOgImageAlt = () => {
          return `${
            type !== 'blog'
              ? ogImageAlt || defaultImageAlt
              : type === 'blogHome'
              ? blogHomeImageAltText
              : feature_image.alt_text
          }`
        }

        const ChineseStyles = (props) => {
          return (
            <Helmet>
              {' '}
              <link
                href="https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@100;400;500;700&display=swap"
                rel="stylesheet"
              />
              <style type="text/css">
                {`
      body {
        font-family: 'Noto Sans SC', sans-serif;
        }
    `}
              </style>
            </Helmet>
          )
        }

        const seo = {
          title: getTitle(),
          description: getDescription(),
          image: getImage(),
          url: getUrl(),
          canonicalUrl: getCanonicalUrl(),
          node_locale: getNodeLocale(),
          ogLocale: getOgLocale(),
          ogType: getOgType(),
          ogTitle: getOgTitle(),
          ogDescription: getOgDescription(),
          ogImage: getOgImage(),
          ogImageWidth: getOgImageWidth(),
          ogImageHeight: getOgImageHeight(),
          ogImageAlt: getOgImageAlt(),
        }

        return (
          <>
            <Helmet>
              <html lang={seo.node_locale} />
              <meta charSet="utf-8" />
              {seo.title && <title>{seo.title}</title>}
              <meta name="Content-Type" content="UTF-8" />
              {seo.description ? (
                <meta name="description" content={seo.description} />
              ) : (
                <meta name="description" content={``} />
              )}

              {!!facebookAppId && (
                <meta name="fb:app_id" content={facebookAppId} />
              )}

              <meta name="image" content={seo.image} />

              {seo.url && <meta property="og:url" content={seo.url} />}

              {hideFromSearch && <meta name="robots" content="noindex" />}

              <link rel="canonical" href={seo.canonicalUrl} />

              <link
                rel="alternate"
                hreflang="x-default"
                href={siteUrl + (slug || '/')}
              />

              <link
                rel="alternate"
                hreflang="zh-cn"
                href={siteUrl + '/zh-cn' + (slug || '/')}
              />

              <meta property="og:locale" content={seo.node_locale} />
              <meta property="og:type" content={seo.ogType} />

              {seo.ogTitle && <meta name="og:title" content={seo.ogTitle} />}

              {seo.description && (
                <meta property="og:description" content={seo.description} />
              )}
              <meta property="og:image" content={seo.ogImage} />

              {ogImageAlt && <meta name="og:image:alt" content={ogImageAlt} />}
              <meta property="og:image:secure_url" content={seo.ogImage} />

              {seo.ogImage && seo.ogImageWidth !== null && (
                <meta property="og:image:width" content={seo.ogImageWidth} />
              )}

              {seo.ogImage && seo.ogImageHeight !== null && (
                <meta property="og:image:height" content={seo.ogImageHeight} />
              )}

              <meta property="og:site_name" content={defaultOgSite_name} />
              <meta name="twitter:card" content={defaultTwitterCard} />
              <meta name="twitter:creator" content={defaultTwitterUserName} />
              <meta name="twitter:site" content={defaultTwitterUserName} />
              {seo.ogTitle && (
                <meta name="twitter:title" content={seo.ogTitle} />
              )}
              {seo.ogDescription && (
                <meta name="twitter:description" content={seo.ogDescription} />
              )}

              {seo.ogImage && (
                <meta name="twitter:image" content={seo.ogImage} />
              )}

              {ogImageAlt && (
                <meta name="twitter:image:alt" content={ogImageAlt} />
              )}

              {seo.ogImage && (
                <meta
                  property="twitter:image:width"
                  content={seo.ogImageWidth}
                />
              )}

              {seo.ogImage && (
                <meta
                  property="twitter:image:height"
                  content={seo.ogImageHeight}
                />
              )}
            </Helmet>
            {seo.node_locale.indexOf('zh-CN') !== -1 && <ChineseStyles />}
          </>
        )
      }}
    />
  )
}

export default SEO

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  pathname: PropTypes.string,
  article: PropTypes.bool,
}

SEO.defaultProps = {
  title: null,
  description: null,
  image: null,
  pathname: null,
  article: false,
}

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        siteUrl
        defaultImage
        defaultImageWidth
        defaultImageHeight
        defaultTwitterUserName
        defaultOgLocale
        defaultOgType
        defaultTwitterCard
        defaultOgSite_name
        defaultImageAlt
        facebookAppId
        blogHomeTitle
        blogHomeDescription
        blogHomeImageUrl
        blogHomeImageWidth
        blogHomeImageHeight
        blogHomeImageAltText
      }
    }
  }
`
